import { useLocale } from '../i18n/LocaleProvider'
import { useEffect, useRef, useState } from 'react'
import './EditorialThumbnail.css'

type Props = {
  src: string
  logo?: boolean
  background?: string
  featured?: boolean
}

export default function EditorialThumbnail(props: Props) {
  // A new source gets a fresh lifecycle, including when a card is reused.
  return <ThumbnailImage key={props.src} {...props} />
}

function ThumbnailImage({ src, logo = false, background, featured = false }: Props) {
  const imageRef = useRef<HTMLImageElement>(null)
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading')

  useEffect(() => {
    const image = imageRef.current
    if (!image) return
    let active = true
    const ready = async () => {
      try { await image.decode() } catch { /* A loaded image can still be displayed if decode rejects. */ }
      if (active) setState(image.naturalWidth > 0 ? 'loaded' : 'error')
    }
    const failed = () => { if (active) setState('error') }
    image.addEventListener('load', ready)
    image.addEventListener('error', failed)
    // Handles cached images and loads completed before hydration.
    if (image.complete) {
      if (image.naturalWidth > 0) void ready()
      else failed()
    }
    return () => {
      active = false
      image.removeEventListener('load', ready)
      image.removeEventListener('error', failed)
    }
  }, [src])

  return (
    <div
      className={`nr-card-thumb editorial-thumbnail editorial-thumbnail--${state}`}
      style={{ background: state === 'error' ? undefined : background }}
      aria-hidden="true"
    >
      {state !== 'error' && (
        <img
          ref={imageRef}
          src={src}
          alt=""
          className={logo ? 'nr-card-logo' : 'nr-card-cover'}
          loading={featured ? 'eager' : 'lazy'}
          decoding="async"
        />
      )}
      {state === 'loading' && <span className="editorial-thumbnail-placeholder editorial-shimmer" />}
      {state === 'error' && <span className="editorial-thumbnail-fallback">Bybeit<span>®</span></span>}
    </div>
  )
}

export function EditorialGridSkeleton() {
  const { t } = useLocale()
  return (
    <div role="status" aria-label={t('Loading stories')} aria-busy="true">
      <div className="nr-grid" aria-hidden="true">
        {[0, 1, 2, 3].map(index => (
          <div key={index} className={`nr-card nr-card--skeleton ${index === 0 ? 'nr-card--featured' : ''}`}>
            <div className="nr-card-thumb editorial-shimmer" />
            <div className="nr-card-body">
              <span className="editorial-skeleton-line editorial-skeleton-category editorial-shimmer" />
              <span className="editorial-skeleton-line editorial-skeleton-title editorial-shimmer" />
              <span className="editorial-skeleton-line editorial-skeleton-title-short editorial-shimmer" />
              {index === 0 && <span className="editorial-skeleton-excerpt">
                <span className="editorial-skeleton-line editorial-shimmer" />
                <span className="editorial-skeleton-line editorial-shimmer" />
                <span className="editorial-skeleton-line editorial-shimmer" />
              </span>}
              <span className="editorial-skeleton-line editorial-skeleton-date editorial-shimmer" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
